import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { useTranslations, useLanguage } from "@vocab/react";
import { Text, Box, IconChevron, Columns, Column, Stack } from "braid-design-system";
import { translations } from "./components/.vocab/index.mjs";
import { JobInformationItem } from "./styles/components/JobInformationItem.mjs";
import { formatMinorUnits } from "@seek/currency";
import { translations as translations$1 } from "./components/PayRange/.vocab/index.mjs";
import { translations as translations$2 } from "./components/PayType/.vocab/index.mjs";
import { translations as translations$3 } from "./JobInformation/.vocab/index.mjs";
const isValidJobClassification = (jobClassification) => Boolean(jobClassification == null ? void 0 : jobClassification.categoryName);
const Classification = ({
  jobClassification
}) => {
  if (!isValidJobClassification(jobClassification)) {
    return null;
  }
  return /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsxs(Box, { "data-testid": "category", children: [
    jobClassification.categoryName,
    jobClassification.subCategoryName && /* @__PURE__ */ jsxs(Box, { marginLeft: "small", "data-testid": "subCategory", children: [
      /* @__PURE__ */ jsx(IconChevron, { tone: "secondary", direction: "right" }),
      ` ${jobClassification.subCategoryName}`
    ] })
  ] }) });
};
const renderClassificationIfValid = (classification) => isValidJobClassification(classification) ? /* @__PURE__ */ jsx(Classification, { jobClassification: classification }) : void 0;
const DateFormat = ({ date }) => {
  const { t } = useTranslations(translations);
  try {
    const dateObject = new Date(date);
    return /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx("time", { dateTime: date, "data-testid": "time", children: t("format date", { dateObject }) }) });
  } catch {
    return null;
  }
};
const renderLocationsIfValid = (locations) => {
  const locationText = locations.filter((location) => location).join(", ");
  return locationText ? /* @__PURE__ */ jsx(Text, { children: locationText }) : void 0;
};
const getPayRangeText = ({
  minLabel,
  maxLabel
}) => {
  if (typeof minLabel === "string" && typeof maxLabel === "string") {
    return minLabel === maxLabel ? `${minLabel}` : `${minLabel} – ${maxLabel}`;
  }
  return minLabel ?? maxLabel ?? null;
};
const PayRange = ({ salary, locale }) => {
  const { t } = useTranslations(translations$1);
  if (!salary || !locale) {
    return null;
  }
  const { salaryCurrency, salaryMin, salaryMax, salaryIsHidden } = salary;
  const formatOptions = {
    currency: salaryCurrency,
    locale,
    display: "code"
  };
  const getNotation = (salaryToBeValidated) => {
    if (salaryToBeValidated > 1e5 || salaryToBeValidated % 100 === 0) {
      return "compact";
    }
    return "standard";
  };
  const minLabel = typeof salaryMin === "number" ? formatMinorUnits(salaryMin, {
    ...formatOptions,
    notation: getNotation(salaryMin)
  }) : null;
  const maxLabel = typeof salaryMax === "number" ? formatMinorUnits(salaryMax, {
    ...formatOptions,
    notation: getNotation(salaryMax)
  }) : null;
  const payRangeText = getPayRangeText({ minLabel, maxLabel });
  if (!payRangeText) {
    return null;
  }
  const isHiddenText = salaryIsHidden ? `(${t("hidden on ad")})` : "";
  return /* @__PURE__ */ jsx(Text, { data: { testid: "payRange" }, children: `${[payRangeText, isHiddenText].join(" ")}` });
};
const renderPayRangeIfValid = (salary, locale = "en-AU") => (salary == null ? void 0 : salary.salaryCurrency) && typeof (salary == null ? void 0 : salary.salaryMin) === "number" && locale ? /* @__PURE__ */ jsx(PayRange, { salary, locale }) : void 0;
const PayType = ({ payType }) => {
  const { t } = useTranslations(translations$2);
  if (!payType) {
    return null;
  }
  const payTypeLabel = t(payType);
  return payTypeLabel ? /* @__PURE__ */ jsx(Text, { data: { testid: "payType" }, children: payTypeLabel }) : null;
};
const renderPayTypeIfValid = (payType) => payType ? /* @__PURE__ */ jsx(PayType, { payType }) : void 0;
var JobAdType = /* @__PURE__ */ ((JobAdType2) => {
  JobAdType2["BRANDED"] = "BRANDED";
  JobAdType2["BRANDED_UPGRADE"] = "BRANDED_UPGRADE";
  JobAdType2["CLASSIC"] = "CLASSIC";
  JobAdType2["CONCIERGE"] = "CONCIERGE";
  JobAdType2["GUARANTEED"] = "GUARANTEED";
  JobAdType2["PREMIUM"] = "PREMIUM";
  return JobAdType2;
})(JobAdType || {});
var WorkType = /* @__PURE__ */ ((WorkType2) => {
  WorkType2["CASUAL"] = "CASUAL";
  WorkType2["CONTRACT_TEMP"] = "CONTRACT_TEMP";
  WorkType2["FULLTIME"] = "FULLTIME";
  WorkType2["PARTTIME"] = "PARTTIME";
  return WorkType2;
})(WorkType || {});
var SalaryType = /* @__PURE__ */ ((SalaryType2) => {
  SalaryType2["ANNUAL_PACKAGE"] = "ANNUAL_PACKAGE";
  SalaryType2["ANNUAL_COMMISSION"] = "ANNUAL_COMMISSION";
  SalaryType2["COMMISSION_ONLY"] = "COMMISSION_ONLY";
  SalaryType2["HOURLY_RATE"] = "HOURLY_RATE";
  SalaryType2["MONTHLY"] = "MONTHLY";
  return SalaryType2;
})(SalaryType || {});
var JobStatus = /* @__PURE__ */ ((JobStatus2) => {
  JobStatus2["ACTIVE"] = "ACTIVE";
  JobStatus2["BLOCKED"] = "BLOCKED";
  JobStatus2["EXPIRED"] = "EXPIRED";
  JobStatus2["PENDING"] = "PENDING";
  JobStatus2["PENDING_ACCOUNT_APPROVAL"] = "PENDING_ACCOUNT_APPROVAL";
  JobStatus2["SUSPENDED"] = "SUSPENDED";
  return JobStatus2;
})(JobStatus || {});
const getProductLabel = (productDisplayName, adType, t) => {
  if (((productDisplayName == null ? void 0 : productDisplayName.length) ?? 0) > 0) {
    return productDisplayName;
  }
  return adType ? t(adType) : void 0;
};
const JobInformation = ({
  jobDetails: job
}) => {
  const { t } = useTranslations(translations$3);
  const { locale } = useLanguage();
  if (!job) {
    return /* @__PURE__ */ jsx(Box, { paddingY: "small", children: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Job information currently unavailable...") }) });
  }
  const {
    id,
    status,
    createdDate,
    expiryDate,
    createdBy,
    adType,
    productDisplayName,
    internalReference,
    workType,
    salary,
    locations,
    classification
  } = job;
  const jobInformationItemsLeft = [
    { label: t("Created"), value: /* @__PURE__ */ jsx(DateFormat, { date: createdDate }) },
    {
      label: status === JobStatus.EXPIRED ? t("Expired") : t("Expiry"),
      value: expiryDate ? /* @__PURE__ */ jsx(DateFormat, { date: expiryDate }) : void 0
    },
    { label: t("Posted by"), value: createdBy },
    {
      label: t("Ad type"),
      value: getProductLabel(productDisplayName, adType, t)
    },
    { label: t("Ad ID"), value: id || void 0 },
    { label: t("Internal reference"), value: internalReference ?? void 0 }
  ].filter((item) => item.value !== void 0);
  const jobInformationItemsRight = [
    { label: t("Work type"), value: workType ? t(workType) : void 0 },
    {
      label: t("Pay Type"),
      value: renderPayTypeIfValid(salary == null ? void 0 : salary.salaryType)
    },
    {
      label: t("Pay Range"),
      value: renderPayRangeIfValid(salary, locale)
    },
    { label: t("Location"), value: renderLocationsIfValid(locations) },
    {
      label: t("Classification"),
      value: renderClassificationIfValid(classification)
    }
  ].filter((item) => item.value !== void 0);
  return /* @__PURE__ */ jsxs(
    Columns,
    {
      space: "gutter",
      collapseBelow: "tablet",
      data: { testid: "jobInformation" },
      children: [
        /* @__PURE__ */ jsx(Column, { width: "2/5", children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: jobInformationItemsLeft.map((item, index) => /* @__PURE__ */ createElement(JobInformationItem, { ...item, key: index })) }) }),
        /* @__PURE__ */ jsx(Column, { width: "3/5", children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: jobInformationItemsRight.map((item, index) => /* @__PURE__ */ createElement(JobInformationItem, { ...item, key: index })) }) })
      ]
    }
  );
};
export {
  JobAdType,
  JobInformation,
  JobStatus,
  SalaryType,
  WorkType
};
